import type { FC } from 'react';
import { lazy, memo, Suspense } from 'react';
import { BiHomeAlt, BiMapAlt, BiUserCircle } from 'react-icons/bi';
import { Link } from 'react-router-dom';

import '../App.scss';
import placement from '../assets/beliggenhed.png';
import quality from '../assets/kvalitet.png';
import heroImage from '../assets/top.webp';
import useGaTracker from '../components/PageTracking';
import { ApartmentInterestForm } from '../components/SignUpForApartment';
import { fetchProperties } from '../components/data/SWR';
import useSeo from '../components/useSeo';
import { strapiUrl } from '../config/env';

const LazyWithSpeechBubbles = lazy(() => import('../components/bubbles'));

interface PropertyProps {
  ejendom: {
    Adresse: string;
    Antal_Lejemaal: number;
    id: number;
    Billede: {
      formats?: {
        small?: {
          url: string;
        };
      };
      url: string;
    };
    Bygget: string;
  };
}

const PropertyPreviewCard: FC<{ property: PropertyProps }> = ({ property }) => {
  const imageUrl =
    property.ejendom.Billede?.formats?.small?.url || property.ejendom.Billede?.url || '';

  return (
    <Link to={`/ejendom/${property.ejendom.id}`}>
      <article className="property-card fade-in">
        <div className="property-card__media">
          {imageUrl ? (
            <img
              alt={`Billede af ${property.ejendom.Adresse}`}
              loading="lazy"
              src={`${strapiUrl}${imageUrl}`}
            />
          ) : null}
        </div>

        <div className="property-card__body">
          <h3>{property.ejendom.Adresse}</h3>
          <p>Velholdt lejebolig centralt i Silkeborg med nem adgang til byliv og natur.</p>

          <div className="property-card__meta">
            <span className="property-pill property-pill--muted">Opført {property.ejendom.Bygget}</span>
            <span className="property-pill">{property.ejendom.Antal_Lejemaal} lejemål</span>
          </div>

          <span className="property-link">
            Se ejendom <span>→</span>
          </span>
        </div>
      </article>
    </Link>
  );
};

const Home: FC = memo(() => {
  const propertiesResponse = fetchProperties();

  useGaTracker();
  useSeo({
    title: 'Toft Ejendomme | Lejligheder i Silkeborg',
    description:
      'Familieejet boligudlejning i Silkeborg med velholdte lejligheder, central beliggenhed og personlig service.',
    path: '/',
  });

  return (
    <>
      <section id="hjem">
        <div className="section-wrap">
          <div className="home-hero home-hero--tall-crop fade-in">
            <div className="hero-copy">
              <span className="eyebrow">Boligudlejning i Silkeborg</span>
              <h1 className="hero-title">
                Find din næste lejlighed <em>centralt i Silkeborg</em>
              </h1>
              <p className="hero-lead">
                Toft Ejendomme ejer og udlejer velholdte lejligheder i Silkeborg. Som familiedrevet
                udlejningsvirksomhed tager vi os personligt af vores bygninger og vores lejere.
              </p>

              <div className="hero-actions">
                <a
                  className="site-btn site-btn--primary"
                  href="https://www.boligportal.dk/dl/toft/"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Se ledige lejemål
                  <svg
                    aria-hidden="true"
                    className="site-btn__icon"
                    fill="none"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M7 17L17 7" stroke="currentColor" strokeLinecap="round" strokeWidth="2" />
                    <path d="M9 7H17V15" stroke="currentColor" strokeLinecap="round" strokeWidth="2" />
                  </svg>
                </a>
                <Link className="site-btn site-btn--outline" to={{ pathname: '/', hash: '#tilmelding' }}>
                  Skriv dig op til venteliste
                </Link>
              </div>
            </div>

            <div
              aria-hidden="true"
              className="hero-visual"
              style={{ backgroundImage: `url(${heroImage})` }}
            >
              <div className="hero-caption">Velholdte boliger tæt på Silkeborg centrum</div>
            </div>
          </div>
        </div>
      </section>

      <section className="stats-bar">
        <div className="section-wrap">
          <div className="stats-grid">
            <div>
              <div className="stat-value">21+</div>
              <span className="stat-label">Ejendomme i Silkeborg</span>
            </div>
            <div>
              <div className="stat-value">100+</div>
              <span className="stat-label">Lejemål i alt</span>
            </div>
            <div>
              <div className="stat-value">60+</div>
              <span className="stat-label">Års erfaring</span>
            </div>
            <div>
              <div className="stat-value" style={{ fontSize: '1.15rem' }}>
                <a href="mailto:info@toftejendomme.dk">info@toftejendomme.dk</a>
              </div>
              <span className="stat-label">Kontakt os</span>
            </div>
          </div>
        </div>
      </section>

      <section className="editorial-section" id="om-os">
        <div className="section-wrap">
          <span className="eyebrow">Om os</span>
          <h2 className="section-title">En lokal udlejer du kan stole på</h2>

          <div className="about-grid" style={{ marginTop: '2.5rem' }}>
            <div className="about-copy">
              <p>
                Toft Ejendomme blev etableret i 1960&apos;erne af malermester Henning Toft. Det var
                starten på en lokal, familieejet ejendomsvirksomhed med rødder i Silkeborg.
              </p>
              <p>
                Gennem generationer har familien Toft videreført virksomheden med fokus på ordentlig
                drift, løbende vedligeholdelse og tæt kontakt til lejerne. Vi ejer og forvalter fortsat alle
                vores ejendomme selv.
              </p>
              <p>
                Vores mål er fortsat enkelt: at tilbyde gode, velholdte boliger til rimelige priser med
                personlig og nærværende service omkring hver ejendom.
              </p>

              <div className="value-grid">
                <article className="value-card">
                  <span className="value-icon">
                    <BiMapAlt />
                  </span>
                  <h3>Central beliggenhed</h3>
                  <p>Alle ejendomme ligger tæt på handel, transport og natur i Silkeborg.</p>
                </article>

                <article className="value-card">
                  <span className="value-icon">
                    <BiHomeAlt />
                  </span>
                  <h3>Velholdte boliger</h3>
                  <p>Vi vedligeholder og renoverer løbende, så boligerne fremstår indbydende.</p>
                </article>

                <article className="value-card">
                  <span className="value-icon">
                    <BiUserCircle />
                  </span>
                  <h3>Personlig service</h3>
                  <p>Som familiedrevet virksomhed er vi tilgængelige og tager os tid til vores lejere.</p>
                </article>
              </div>
            </div>

            <aside className="quote-card">
              <blockquote>
                "Vi ejer og driver alle vores bygninger selv — det giver os et direkte og ansvarligt
                forhold til vores lejere."
              </blockquote>
              <cite>— Toft Ejendomme</cite>
            </aside>
          </div>
        </div>
      </section>

      <section className="editorial-section section-alt" id="ejendomme">
        <div className="section-wrap">
          <div className="section-header">
            <div>
              <span className="eyebrow">Vores ejendomme</span>
              <h2 className="section-title">Kvalitetsboliger i Silkeborg</h2>
              <p className="page-flow__lead">Her ser du et udvalg af vores ejendomme i Silkeborg.</p>
            </div>
            <Link className="site-btn site-btn--outline" to="/ejendomme">
              Se alle ejendomme →
            </Link>
          </div>

          <div className="property-grid">
            {propertiesResponse.data?.Ejendom?.slice(0, 3).map((property: PropertyProps) => (
              <PropertyPreviewCard key={property.ejendom.id} property={property} />
            ))}
          </div>
        </div>
      </section>

      <section className="editorial-section" id="tilmelding">
        <div className="section-wrap">
          <div className="signup-grid">
            <div className="signup-copy">
              <span className="eyebrow">Venteliste</span>
              <h2 className="section-title">Skriv dig op</h2>
              <p>
                Ledige lejligheder udbydes løbende via Boligportal. Du kan også skrive dig op til vores
                venteliste herunder, så kontakter vi dig, når der dukker en bolig op, der passer til dine
                ønsker.
              </p>
              <p>
                Fortæl os, hvad du leder efter, og hvor længe du ønsker at stå aktivt på ventelisten.
              </p>
            </div>

            <ApartmentInterestForm />
          </div>
        </div>
      </section>

      <Suspense
        fallback={
          <section className="editorial-section section-alt">
            <div className="section-wrap">
              <p className="section-lead" style={{ margin: 0 }}>
                Indlæser anbefalinger…
              </p>
            </div>
          </section>
        }
      >
        <LazyWithSpeechBubbles />
      </Suspense>

      <section className="editorial-section">
        <div className="section-wrap">
          <div className="feature-grid">
            <article className="feature-card feature-card--light">
              <div className="feature-card__media" style={{ backgroundImage: `url(${quality})` }} />
              <div className="feature-card__content">
                <span className="feature-card__icon">
                  <BiHomeAlt />
                </span>
                <h3>Kvalitet</h3>
                <p>
                  Vi stræber efter at vedligeholde vores ejendomme i den bedste kvalitet, så alle boliger
                  fremstår flotte og velrenoverede.
                </p>
              </div>
            </article>

            <article className="feature-card feature-card--light">
              <div className="feature-card__media" style={{ backgroundImage: `url(${placement})` }} />
              <div className="feature-card__content">
                <span className="feature-card__icon">
                  <BiMapAlt />
                </span>
                <h3>Beliggenhed</h3>
                <p>
                  Vores ejendomme ligger i Silkeborgs attraktive midt- og sydby med kort afstand til
                  midtbyens tilbud.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>
    </>
  );
});

Home.displayName = 'Home';

export default Home;
